import { Injectable, inject, Injector } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable, of } from 'rxjs';
import { LocalStorageService } from './storage/local-storage.service';
import { environment } from '@env/environment';
import { CropMapEnterpiseListViewModel, EnterpriseUserAccessViewModel, UserProxy } from '../../proxy/proxies';
import { CropMapAccessService } from './cropmap-access.service';
import { AuthService } from './auth.service';

export interface CropMapUserEnterpiseListViewModel {
    userId?: string;
    userName?: string;
    userSurname?: string;
    enterprises?: any[];
    [key: string]: any;
}

/** Options for change-enterprise (from GET /v4.0/crop-map-user/enterprise). */
export interface CropMapEnterpriseOption {
    enterpriseId: string;
    name?: string;
    hasCropMapAccess?: boolean;
    cropMapAccessRoleId?: string;
}

interface UserCache {
    currentUser: CropMapUserEnterpiseListViewModel | null;
    profile: CropMapUserEnterpiseListViewModel | null;
}

interface RegistrationCache {
    userId: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class UserService {
    viewAllUser: boolean = false;
    private initialized = false;

    private readonly userCache = new BehaviorSubject<UserCache>({
        currentUser: null,
        profile: null,
    });

    private readonly registrationCache = new BehaviorSubject<RegistrationCache>({
        userId: null,
    });

    private readonly cropMapEnterpriseOptionsSubject = new BehaviorSubject<CropMapEnterpriseOption[]>([]);

    selectedEnterprise: EnterpriseUserAccessViewModel | null = null;
    private selectedEnterpriseSubject = new BehaviorSubject<EnterpriseUserAccessViewModel | null>(null);
    selectedEnterprise$ = this.selectedEnterpriseSubject.asObservable();
    get selectedEnterpriseId(): string {
        return this.selectedEnterprise?.id as string;
    }

    private readonly _cropMapAccessService = inject(CropMapAccessService);
    private readonly _authService = inject(AuthService);
    private readonly _userProxy = inject(UserProxy);

    constructor(private localStorageService: LocalStorageService) {}

    markInitialized(): void {
        this.initialized = true;
    }

    private updateCache(data: Partial<UserCache>): void {
        const currentCache = this.userCache.value;
        const newCache: UserCache = {
            ...currentCache,
            ...data,
        };
        this.userCache.next(newCache);
    }

    clearCache(): void {
        this.initialized = false;
        this.userCache.next({
            currentUser: null,
            profile: null,
        });
        this.cropMapEnterpriseOptionsSubject.next([]);
    }

    /**
     * True when the user has at least one enterprise with CropMap access
     * ({@link CropMapEnterpriseOption.hasCropMapAccess} from API).
     */
    hasCropMapEnterpriseAccess(): boolean {
        return this.cropMapEnterpriseOptionsSubject.value.length > 0;
    }

    /** Populates enterprises from CropMapUserProxy.cropMapUser_GetCropMapEnterpriseByUser(). */
    setCropMapEnterpriseOptions(
        enterprises:
            | Pick<
                  EnterpriseUserAccessViewModel,
                  'id' | 'name' | 'hasCropMapAccess' | 'cropMapAccessRoles'
              >[]
            | undefined
    ): void {
        const list: CropMapEnterpriseOption[] = (enterprises ?? [])
            .filter((e) => e.hasCropMapAccess === true)
            .map((e) => ({
                enterpriseId: e.id,
                name: e.name,
                hasCropMapAccess: e.hasCropMapAccess,
                cropMapAccessRoleId: e.cropMapAccessRoles?.[0]?.id,
            }));
        this.cropMapEnterpriseOptionsSubject.next(list);

        const withCropMapAccess = list.filter((e) => e.hasCropMapAccess === true);
        if (withCropMapAccess.length === 0) {
            return;
        }

        const currentId = this.localStorageService.getItem<string>('enterpriseId');
        const currentIsValid =
            !!currentId && withCropMapAccess.some((e) => e.enterpriseId === currentId);

        if (!currentIsValid) {
            this.localStorageService.setItem('enterpriseId', withCropMapAccess[0].enterpriseId);
        }
    }

    setSelectedEnterprise(enterprise: EnterpriseUserAccessViewModel) {
        if (!enterprise?.id || !enterprise?.cropMapAccessRoles?.[0]?.id) {
            this.clearSelectedEnterprise();
            return;
        }
        this.selectedEnterprise = enterprise;
        this.selectedEnterpriseSubject.next(enterprise);
        this.localStorageService.setItem('selectedEnterprise', enterprise);
        this.localStorageService.setItem('selectedEnterpriseId', enterprise.id);
    }

    get cropMapEnterpriseOptions$(): Observable<CropMapEnterpriseOption[]> {
        return this.cropMapEnterpriseOptionsSubject.asObservable();
    }

    clearRegistrationCache(): void {
        this.registrationCache.next({
            userId: null,
        });
    }

    setCurrentUser(user: CropMapUserEnterpiseListViewModel | null): void {
        this.updateCache({ currentUser: user });
    }

    get registrationUserId$(): Observable<string | null> {
        return this.registrationCache.pipe(map((cache) => cache.userId));
    }

    get currentUser$(): Observable<CropMapUserEnterpiseListViewModel | null> {
        return this.userCache.pipe(map((cache) => cache.currentUser));
    }

    get userProfile$(): Observable<CropMapUserEnterpiseListViewModel | null> {
        return this.userCache.pipe(map((cache) => cache.profile));
    }

    get currentUser(): CropMapUserEnterpiseListViewModel | null {
        return this.userCache.value.currentUser;
    }

    get userProfile(): CropMapUserEnterpiseListViewModel | null {
        return this.userCache.value.profile;
    }

    getUserFullName(): string {
        const user = this.currentUser;
        if (!user?.userName) return '';
        return user.userName.trim();
    }

    getUserFirstName(): string {
        const user = this.currentUser;
        if (!user?.userName) return '';
        return user.userName || '';
    }

    isInitialized(): boolean {
        return this.initialized;
    }

    getAppId(): string {
        return environment.appId;
    }

    clearSelectedEnterprise(): void {
        this.selectedEnterprise = null;
        this.selectedEnterpriseSubject.next(null);
        this.localStorageService.removeItem('selectedEnterprise');
        this.localStorageService.removeItem('enterpriseId');
    }

    logout(): void {
        this._userProxy
            .user_Logout()
            .pipe(
                finalize(() => {
                    this.clearCache();
                    this.clearRegistrationCache();
                    this.clearSelectedEnterprise();
                    this._authService.logOut();
                }),
            )
            .subscribe();
    }
}
